// ─────────────────────────────────────────────────────────────
// DARK THEME CORRECTIONS  (dark mode only; light mode is untouched)
//
// Most screens colour themselves through the CSS variables in styles.js and switch cleanly.
// Many older screens set colours inline instead — a white table row, a #c4d4f6 card,
// #1e293b text — and those never change, so in dark mode they show as white blocks or as
// light text on white. Rather than edit every screen (and risk the light theme), a small
// observer runs only while dark mode is on: it reads each element's inline background,
// text and border colour, decides whether it is a light surface / dark text / a status tint,
// and tags the element (data-dk-bg, data-dk-fg, data-dk-bd). The CSS below maps those tags
// to the dark palette. Colours changed later (hover handlers) are re-read. Printing and
// anything inside [data-keep-light] are left alone.
// ─────────────────────────────────────────────────────────────

const parse = v => {
  const m = /rgba?\(\s*(\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/.exec(v || '');
  if (m) return { r: +m[1], g: +m[2], b: +m[3], a: m[4] === undefined ? 1 : +m[4] };
  if (v === 'white') return { r: 255, g: 255, b: 255, a: 1 };
  if (v === 'black') return { r: 0, g: 0, b: 0, a: 1 };
  return null;
};
const hsl = ({ r, g, b }) => {
  const [R, G, B] = [r / 255, g / 255, b / 255];
  const max = Math.max(R, G, B), min = Math.min(R, G, B), l = (max + min) / 2, d = max - min;
  if (!d) return { h: 0, s: 0, l };
  const s = d / (1 - Math.abs(2 * l - 1));
  let h = max === R ? ((G - B) / d) % 6 : max === G ? (B - R) / d + 2 : (R - G) / d + 4;
  h = (h * 60 + 360) % 360;
  return { h, s, l };
};
const hue = h => (h < 15 || h >= 330 ? 'red' : h < 70 ? 'amber' : h < 170 ? 'green' : h < 200 ? 'cyan' : h < 255 ? 'blue' : 'purple');

// light surfaces -> card / elevated / hover; pale tints -> a dark tint of the same hue
function bgTag(c) {
  if (!c || c.a < 0.5) return null;
  const { h, s, l } = hsl(c);
  if (l < 0.78) return null; // already a mid/dark colour (brand navy, status fills): keep
  if (s < 0.2 || l >= 0.985) return l >= 0.975 ? 'card' : l >= 0.92 ? 'elevated' : 'hover';
  return hue(h);
}
// dark neutral text -> light text; dark coloured text -> the light variant of its hue
function fgTag(c) {
  if (!c) return null;
  const { h, s, l } = hsl(c);
  if (s < 0.25) return l < 0.26 ? 'primary' : l < 0.56 ? 'secondary' : null;
  return l < 0.45 ? hue(h) : null;
}
function bdTag(c) {
  if (!c) return null;
  const { s, l } = hsl(c);
  if (l < 0.16) return 'strong';
  if (l > 0.78 && s < 0.35) return 'line';
  return null;
}

const ATTRS = [['dkBg', el => bgTag(parse(el.style.backgroundColor))], ['dkFg', el => fgTag(parse(el.style.color))],
  ['dkBd', el => bdTag(parse(el.style.borderTopColor || el.style.borderBottomColor || el.style.borderLeftColor || el.style.borderColor))]];

function tag(el) {
  if (!(el instanceof HTMLElement) || el.closest('[data-keep-light]')) return;
  ATTRS.forEach(([key, fn]) => {
    const v = fn(el);
    if (v) { if (el.dataset[key] !== v) el.dataset[key] = v; } else if (key in el.dataset) delete el.dataset[key];
  });
}
const scan = root => {
  if (root instanceof HTMLElement && root.hasAttribute('style')) tag(root);
  root.querySelectorAll?.('[style]').forEach(tag);
};

let observer = null;
// start / stop with the theme; App sets data-theme on <html>
export function syncDarkFixer(theme) {
  if (theme === 'dark' && !observer) {
    scan(document.body);
    observer = new MutationObserver(list => {
      list.forEach(m => {
        if (m.type === 'attributes') tag(m.target);
        else m.addedNodes.forEach(n => n.nodeType === 1 && scan(n));
      });
    });
    observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['style'] });
  } else if (theme !== 'dark' && observer) {
    observer.disconnect();
    observer = null;
  }
}

const D = '[data-theme="dark"]';
const TINT = { red: '251, 111, 140', amber: '251, 191, 36', green: '52, 211, 153', cyan: '56, 214, 238', blue: '96, 165, 250', purple: '165, 180, 252' };
const LIGHT_TEXT = { red: 'var(--red)', amber: 'var(--amber)', green: 'var(--green)', cyan: 'var(--cyan)', blue: '#8fb3ff', purple: 'var(--purple)' };

export const DARK_THEME_CSS = `
/* ── dark theme corrections (src/darkTheme.js) — screen only, never in print ── */
@media screen {
${D} [data-dk-bg="card"] { background-color: var(--bg-card) !important; }
${D} [data-dk-bg="elevated"] { background-color: var(--bg-elevated) !important; }
${D} [data-dk-bg="hover"] { background-color: var(--bg-hover) !important; }
${Object.entries(TINT).map(([k, rgb]) => `${D} [data-dk-bg="${k}"] { background-color: rgba(${rgb}, 0.15) !important; }`).join('\n')}
${D} [data-dk-fg="primary"] { color: var(--text-primary) !important; }
${D} [data-dk-fg="secondary"] { color: var(--text-secondary) !important; }
${Object.entries(LIGHT_TEXT).map(([k, c]) => `${D} [data-dk-fg="${k}"] { color: ${c} !important; }`).join('\n')}
${D} [data-dk-bd="strong"] { border-color: var(--border-strong) !important; }
${D} [data-dk-bd="line"] { border-color: var(--border) !important; }

/* native form controls */
${D} input:not([type=checkbox]):not([type=radio]):not([type=range]), ${D} select, ${D} textarea { color-scheme: dark; }
${D} ::placeholder { color: var(--text-muted); opacity: 1; }

/* ── HR & People: its stylesheet (src/hr/hr.css) has its own palette under .hr-scope ── */
${D} .hr-scope, ${D} .hr-portal {
  --bg: var(--bg-base); --panel: var(--bg-card); --ink: var(--text-primary); --ink-2: #c3d2dc; --muted: var(--text-secondary);
  --line: var(--border); --brand: #8fb3ff; --brand-soft: rgba(96, 165, 250, 0.16); --head: #8fb3ff; --side-deep: #e7f1f6;
  --card-border: var(--border-strong); --grid-line: var(--border-strong);
  --good-bg: rgba(52, 211, 153, 0.14); --warn-bg: rgba(251, 191, 36, 0.14); --bad-bg: rgba(251, 111, 140, 0.14);
  --info-bg: rgba(96, 165, 250, 0.14); --cyan-bg: rgba(56, 214, 238, 0.13); --amber-bg: rgba(251, 191, 36, 0.14);
  --good: var(--green); --warn: var(--amber); --bad: var(--red); --info: #8fb3ff;
}
${D} .hr-scope input, ${D} .hr-scope select, ${D} .hr-scope textarea { background: var(--bg-surface); color: var(--text-primary); border-color: var(--border-light); }
${D} .hr-scope .dropzone, ${D} .hr-scope .photo-card, ${D} .hr-scope .doc-card, ${D} .hr-scope .file-chip, ${D} .hr-scope .chip,
${D} .hr-scope .toggle-card { background: var(--bg-elevated); color: var(--text-primary); }
${D} .hr-scope table td { background-color: var(--bg-card); color: var(--text-primary); border-color: var(--border-light); }
${D} .hr-scope table tr:nth-child(even) td { background-color: var(--bg-elevated); }
${D} .hr-scope .photo-preview, ${D} .hr-scope .dc-cell.not { background: var(--bg-hover); color: var(--text-secondary); }
${D} .hr-scope .btn:not(.primary):not(.danger):not(.success) { background: var(--bg-elevated); color: var(--text-primary); border-color: var(--border-light); }
${D} .hr-scope .file-chip.pending, ${D} .hr-scope label.photo-card.pending { background: rgba(251, 191, 36, 0.12); }
${D} .hr-scope .kpi, ${D} .hr-scope .em-kpi, ${D} .hr-scope .panel, ${D} .hr-scope .nav-card { background: var(--bg-card); color: var(--text-primary); }

/* Pastel colour-coded cards (role cards on My Profile and Role Master). Their light pastel
   backgrounds (yellows especially) do not survive the automatic remap, so in dark mode the
   colour moves to the border: a dark card with a faint tint of it, and readable text.
   The card sets --tint to its accent colour. Doubled class = beats the generic [data-dk-*] rules. */
${D} .dk-tint-card.dk-tint-card {
  background: color-mix(in srgb, var(--tint) 10%, var(--bg-card)) !important;
  border-color: var(--tint) !important;
}
${D} .dk-tint-card.dk-tint-card .dk-tint-title { color: var(--text-primary) !important; }
${D} .dk-tint-card.dk-tint-card .dk-tint-sub { color: var(--text-secondary) !important; }
${D} .dk-tint-card.dk-tint-card.dk-tint-text { color: #ffffff !important; }
}
`;
